import React, { useEffect, useState } from 'react';
import { G, styles, api } from '../theme.js';
import { Btn, Badge, Input, Select, Field, Modal, Spinner } from '../ui.jsx';
import { PhotoPicker } from '../widgets/PhotoPicker.jsx';

const COND_COLOR = { excellent:G.green, good:G.cyan, fair:G.yellow, poor:G.red, unknown:G.textMuted };

export function Equipment({ toast }) {
  const [equipment, setEquipment] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = () => Promise.all([api('/api/equipment'), api('/api/contacts').catch(()=>[])])
    .then(([e,c]) => { setEquipment(e); setContacts(c); })
    .catch(e=>toast(e.message,'error')).finally(()=>setLoading(false));
  useEffect(() => { load(); }, []);

  const remove = async e => {
    if (!confirm('Delete this equipment record?')) return;
    try { await api(`/api/equipment/${e.id}`, { method:'DELETE' }); load(); toast('Deleted'); }
    catch(err) { toast(err.message,'error'); }
  };

  const warrantyExpiring = equipment.filter(e => e.warrantyDaysLeft !== null && e.warrantyDaysLeft > 0 && e.warrantyDaysLeft < 90);
  const serviceDue = equipment.filter(e => e.nextServiceDays !== null && e.nextServiceDays < 60);

  if (loading) return <Spinner sz={32}/>;

  return <div style={{ padding:24 }}>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
      <h1 style={{ margin:0, fontSize:24, fontWeight:800 }}>Equipment <span style={{ color:G.textMuted, fontWeight:500, fontSize:18 }}>({equipment.length})</span></h1>
      <Btn v="primary" onClick={()=>setEditing({ type:'AC', condition:'good' })}>+ New Equipment</Btn>
    </div>

    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))', gap:14, marginBottom:18 }}>
      <Stat label="Total Units" value={equipment.length} sub="across all customers" color={G.cyan}/>
      <Stat label="Service Due" value={serviceDue.length} sub="within 60 days" color={G.yellow}/>
      <Stat label="Warranty Expiring" value={warrantyExpiring.length} sub="within 90 days" color={G.orange}/>
      <Stat label="Poor/Fair Condition" value={equipment.filter(e=>['poor','fair'].includes(e.condition)).length} sub="upgrade candidates" color={G.red}/>
    </div>

    <div style={{ ...styles.card, padding:0, overflow:'hidden' }}>
      <table style={{ width:'100%', borderCollapse:'collapse' }}>
        <thead><tr>{['Customer','Type','Make/Model','Serial #','Installed','Warranty','Condition','Next Service',''].map(h => <th key={h} style={styles.th}>{h}</th>)}</tr></thead>
        <tbody>{equipment.map(e => (
          <tr key={e.id} onClick={()=>setEditing(e)} style={{ cursor:'pointer' }}>
            <td style={styles.td}>
              <div style={{ fontWeight:600 }}>{e.contactName||'—'}</div>
              <div style={{ fontSize:12, color:G.textMuted }}>{e.address||''}</div>
            </td>
            <td style={styles.td}>{e.type}{e.tonnage?` · ${e.tonnage}-ton`:''}</td>
            <td style={styles.td}>{e.manufacturer} {e.model}</td>
            <td style={{ ...styles.td, fontFamily:'monospace', fontSize:11 }}>{e.serialNumber||'—'}</td>
            <td style={{ ...styles.td, fontSize:12 }}>{e.installDate||'—'}</td>
            <td style={{ ...styles.td, fontSize:12 }}>
              {e.warrantyExpiry ? <span style={{ color: e.warrantyDaysLeft<0 ? G.red : e.warrantyDaysLeft<90 ? G.orange : G.green }}>
                {e.warrantyDaysLeft<0 ? `Expired ${-e.warrantyDaysLeft}d ago` : `${e.warrantyDaysLeft}d left`}
              </span> : '—'}
            </td>
            <td style={styles.td}><Badge c={COND_COLOR[e.condition]} bg={COND_COLOR[e.condition]+'22'}>{e.condition}</Badge></td>
            <td style={{ ...styles.td, fontSize:12 }}>
              {e.nextServiceDue ? <span style={{ color: e.nextServiceDays<14?G.red:e.nextServiceDays<60?G.yellow:G.text }}>
                {e.nextServiceDays<0?`Overdue ${-e.nextServiceDays}d`:`In ${e.nextServiceDays}d`}
              </span> : '—'}
            </td>
            <td style={styles.td} onClick={ev=>ev.stopPropagation()}><Btn sz="xs" v="danger" onClick={()=>remove(e)}>×</Btn></td>
          </tr>
        ))}</tbody>
      </table>
      {equipment.length === 0 && <div style={{ padding:48, textAlign:'center', color:G.textMuted }}>No equipment yet. Track customer-owned units for membership upsells and warranty alerts.</div>}
    </div>

    <EquipmentModal eq={editing} contacts={contacts} onClose={()=>setEditing(null)} onSaved={()=>{ load(); setEditing(null); toast('Saved'); }}/>
  </div>;
}

function EquipmentModal({ eq, contacts, onClose, onSaved }) {
  const [f, setF] = useState({});
  useEffect(() => { setF(eq||{}); }, [eq]);
  if (!eq) return null;
  const isNew = !f.id;
  const save = async () => {
    try {
      if (isNew) await api('/api/equipment', { method:'POST', body: f });
      else await api(`/api/equipment/${f.id}`, { method:'PATCH', body: f });
      onSaved();
    } catch(e) { alert(e.message); }
  };
  return <Modal open onClose={onClose} width={680}>
    <h2 style={{ margin:'0 0 14px 0' }}>{isNew?'New Equipment':`${f.manufacturer||''} ${f.model||''}`}</h2>
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
      <Field label="Customer">
        <Select value={f.contactId||''} onChange={e=>setF({...f,contactId:e.target.value})}>
          <option value="">— select —</option>
          {contacts.map(c => <option key={c.id} value={c.id}>{c.firstName} {c.lastName}</option>)}
        </Select>
      </Field>
      <Field label="Type *">
        <Select value={f.type||'AC'} onChange={e=>setF({...f,type:e.target.value})}>
          {['AC','Furnace','Heat Pump','Mini Split','Boiler','Water Heater','Air Handler','Other'].map(t => <option key={t}>{t}</option>)}
        </Select>
      </Field>
      <Field label="Manufacturer"><Input value={f.manufacturer||''} onChange={e=>setF({...f,manufacturer:e.target.value})}/></Field>
      <Field label="Model"><Input value={f.model||''} onChange={e=>setF({...f,model:e.target.value})}/></Field>
      <Field label="Serial Number"><Input value={f.serialNumber||''} onChange={e=>setF({...f,serialNumber:e.target.value})}/></Field>
      <Field label="Location at site"><Input value={f.location||''} onChange={e=>setF({...f,location:e.target.value})}/></Field>
      <Field label="Install Date"><Input type="date" value={f.installDate||''} onChange={e=>setF({...f,installDate:e.target.value})}/></Field>
      <Field label="Warranty Expires"><Input type="date" value={f.warrantyExpiry||''} onChange={e=>setF({...f,warrantyExpiry:e.target.value})}/></Field>
      <Field label="Last Service"><Input type="date" value={f.lastServiceDate||''} onChange={e=>setF({...f,lastServiceDate:e.target.value})}/></Field>
      <Field label="Next Service Due"><Input type="date" value={f.nextServiceDue||''} onChange={e=>setF({...f,nextServiceDue:e.target.value})}/></Field>
      <Field label="Tonnage"><Input type="number" step="0.5" value={f.tonnage||''} onChange={e=>setF({...f,tonnage:e.target.value?Number(e.target.value):null})}/></Field>
      <Field label="SEER"><Input type="number" value={f.seer||''} onChange={e=>setF({...f,seer:e.target.value?Number(e.target.value):null})}/></Field>
      <Field label="Refrigerant"><Input value={f.refrigerant||''} onChange={e=>setF({...f,refrigerant:e.target.value})}/></Field>
      <Field label="Condition">
        <Select value={f.condition||'good'} onChange={e=>setF({...f,condition:e.target.value})}>
          <option>excellent</option><option>good</option><option>fair</option><option>poor</option><option>unknown</option>
        </Select>
      </Field>
    </div>
    <Field label="Notes"><Input value={f.notes||''} onChange={e=>setF({...f,notes:e.target.value})}/></Field>

    {/* Receipts + photos — equipment photos, install pics, purchase receipts */}
    <Field label="Photos & Receipts">
      <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
        <PhotoPicker label="📷 Add Photo / Receipt" upload multiple toast={(m,t)=>alert(m)} onPicked={(uploaded) => {
          const list = Array.isArray(uploaded) ? uploaded : [uploaded];
          const existing = Array.isArray(f.attachments) ? f.attachments : [];
          setF({...f, attachments: [...existing, ...list.filter(Boolean).map(u => ({ url: u.url, name: u.name, kind: u.mime }))]});
        }}/>
        {(f.attachments||[]).length > 0 && <span style={{ fontSize:12, color:G.textMuted }}>{(f.attachments||[]).length} attached</span>}
      </div>
      {(f.attachments||[]).length > 0 && <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(80px, 1fr))', gap:6, marginTop:10 }}>
        {f.attachments.map((a, i) => (
          <div key={i} style={{ position:'relative', borderRadius:6, overflow:'hidden', background:G.input, aspectRatio:'1' }}>
            {(/image/i.test(a.kind||'')) ? <img src={a.url} alt={a.name||''} style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
              : <a href={a.url} target="_blank" rel="noreferrer" style={{ display:'flex', alignItems:'center', justifyContent:'center', width:'100%', height:'100%', fontSize:11, color:G.textMuted, textDecoration:'none' }}>📄 {a.name?.slice(0,12)||'file'}</a>}
            <button onClick={()=>setF({...f, attachments: f.attachments.filter((_,j)=>j!==i)})} style={{ position:'absolute', top:2, right:2, background:'rgba(0,0,0,.6)', color:'#fff', border:'none', borderRadius:'50%', width:20, height:20, cursor:'pointer', fontSize:11 }}>×</button>
          </div>
        ))}
      </div>}
    </Field>

    <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:14 }}>
      <Btn onClick={onClose}>Cancel</Btn>
      <Btn v="primary" onClick={save}>Save</Btn>
    </div>
  </Modal>;
}

function Stat({ label, value, sub, color }) {
  return <div style={{ ...styles.card, borderColor:color+'66' }}>
    <div style={{ fontSize:11, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.05em' }}>{label}</div>
    <div style={{ fontSize:24, fontWeight:800, color, marginTop:6 }}>{value}</div>
    <div style={{ fontSize:12, color:G.textMuted, marginTop:4 }}>{sub}</div>
  </div>;
}
